import { ReactNode, useEffect, useMemo } from 'react';
import * as Cesium from "cesium";
import useScenario from '../hooks/useScenario';
import useUrlParams from '../hooks/useUrlParams';
import useViewer from '../hooks/useViewer';
import useOrbit from '../hooks/useOrbit';
import useLoader from '../hooks/useLoader';
import { isOperateMode, isSummaryMode } from '../hooks/utils';
import { SCENARIO_DEPLOYMENT_TYPES } from '../../simulate/constants';
import useUrlNavigation from '../hooks/useUrlNavigation';

interface CesiumProviderProps {
    children: ReactNode;
}

const CesiumProvider = ({ children }: CesiumProviderProps) => {
    const { orbitData } = useOrbit()
    const { setLoading } = useLoader()
    const { navigate_to } = useUrlNavigation()
    const { dashboard, tab_name } = useUrlParams()
    const { scenario, get_simulation_time, getUpdatedSimulationTime } = useScenario()
    const { viewer, multiplier, set_current_simulation_time, set_multiplier, set_viewer_state } = useViewer()
    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
    const isValidateSummary = deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE && dashboard === 'summary'

    const { simulation_start_time, simulation_end_time_milisec, simulation_start_time_milisec, simulation_duration, manual_change } = useMemo(() => {
        if (!scenario || !orbitData || !viewer) {
            return {
                simulation_start_time: 0,
                simulation_end_time: 0,
                simulation_start_time_milisec: 0,
                simulation_end_time_milisec: 0,
                simulation_duration: 0,
                manual_change: false
            }
        }
        return get_simulation_time(scenario, orbitData, viewer)
    }, [scenario, orbitData, viewer])

    useEffect(() => {
        if (!scenario || viewer || isValidateSummary) return
        if (!viewer && !isValidateSummary && tab_name !== 'hud') {
            return navigate_to('hud')
        }
        else return
    }, [tab_name, viewer, isValidateSummary, scenario])

    useEffect(() => {
        if (!isValidateSummary) return
        setLoading('viewer', false)
        setLoading('timeline', false)
    }, [isValidateSummary])

    useEffect(() => {
        if (isValidateSummary) return
        if (!viewer || !scenario || !simulation_start_time || !simulation_end_time_milisec || !dashboard) return
        try {
            if (dashboard === 'quick-deploy') return
            const Interval = setInterval(() => {
                const clock = viewer?.clock;
                if (clock && clock?.currentTime) {
                    const currentTime = clock?.currentTime;
                    const epochTime = multiplier > 0 ? Cesium.JulianDate.toDate(currentTime).getTime() + (multiplier * 1000) : Cesium.JulianDate.toDate(currentTime).getTime()
                    const operateMode = isOperateMode(dashboard, scenario)
                    const deploymentType = scenario?.scenarioDeploymentType?.toUpperCase()
                    const useScenarioTimeLogic = deploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER || deploymentType === SCENARIO_DEPLOYMENT_TYPES.VALIDATE
                    const summaryMode = isSummaryMode(dashboard, scenario)
                    const shouldUseOperateLogic = operateMode && !useScenarioTimeLogic && !summaryMode
                    if (epochTime >= simulation_end_time_milisec && shouldUseOperateLogic) {
                        const currentTime = epochTime / 1000
                        if (!manual_change) {
                            getUpdatedSimulationTime(scenario, currentTime, currentTime, simulation_duration)
                        }
                        set_current_simulation_time(epochTime)
                    }
                    else if (epochTime >= simulation_end_time_milisec && dashboard === 'simulation') {
                        clearInterval(Interval);
                        set_current_simulation_time(simulation_end_time_milisec)
                    }
                    else {
                        const newCurrentTime = epochTime <= simulation_start_time_milisec ? simulation_start_time_milisec : epochTime >= simulation_end_time_milisec ? simulation_end_time_milisec : epochTime
                        set_current_simulation_time(newCurrentTime)
                        const current_time = epochTime / 1000
                        if (current_time <= simulation_start_time && multiplier < 0) {
                            set_multiplier(0)
                            set_viewer_state('pause')
                        }
                    }

                }
            }, 1000);

            return () => {
                clearInterval(Interval);
            };
        } catch {
            // setInterval setup error — should not occur in practice
        } finally {
            setLoading('timeline', false)
        }
    }, [isValidateSummary, scenario, simulation_start_time, simulation_end_time_milisec, simulation_start_time_milisec, viewer, dashboard, multiplier, manual_change])

    return children
}

export default CesiumProvider
